import { createSlice, createEntityAdapter } from '@reduxjs/toolkit'
import {
  searchFacilityProfiles,
  fetchFacilityById,
  updateFacility,
  getRelatedFacilities,
  getRelatedDoctors,
  createFacility,
  deleteFacility,
  fetchFacilityProfileById,
  createFacilityProfile,
  updateFacilityProfile,
  setFacilityProfileRetriever,
  setFacilityProfileSpecialReleaseRetriever,
  deleteAuthForm,
  deleteSpecialReleaseForm,
  fetchFormById,
  getRelatedFacilityProfileFacilities,
  fetchFacilityProfileUserNotesById,
  addFacilityProfileUserNote,
  approveFacilityProfileUserNote,
  updateFacilityProfileUserNote,
  deleteFacilityProfileUserNoteById,
  resetState,
} from './actions'
import {
  IDLE_STATUS,
  LOADING_STATUS,
  SUCCESS_STATUS,
  FAILURE_STATUS,
} from '../../../constants'

export const facilityAdapter = createEntityAdapter()
export const facilityProfileAdapter = createEntityAdapter()

export const userNotesAdapter = createEntityAdapter({
  selectId: note => note.id,
})

const initialState = facilityAdapter.getInitialState({
  status: IDLE_STATUS,
  related_facilities: facilityAdapter.getInitialState({ count: 0 }),
  related_doctors: facilityAdapter.getInitialState({ count: 0 }),
  facility_profiles: facilityProfileAdapter.getInitialState({ count: 0 }),
  related_facility_profiles: facilityAdapter.getInitialState({ count: 0 }),
  user_notes: userNotesAdapter.getInitialState({
    status: IDLE_STATUS,
    error: null,
    count: 0,
    currentPage: 1,
    currentPageSize: 10,
  }),
})

const facilitySlice = createSlice({
  name: 'facilities',
  initialState,
  extraReducers: builder => {
    builder.addCase(resetState, state => {
      state.related_facilities = facilityAdapter.getInitialState({ count: 0 })
      state.related_doctors = facilityAdapter.getInitialState({ count: 0 })
      state.facility_profiles = facilityProfileAdapter.getInitialState({ count: 0 })
      state.related_facility_profiles = facilityAdapter.getInitialState({ count: 0 })
      state.status = IDLE_STATUS
      facilityAdapter.removeAll(state)
    })

    // --- Facility Profile Actions ---
    builder.addCase(searchFacilityProfiles.pending, state => {
      state.status = LOADING_STATUS
    })
    builder.addCase(
      searchFacilityProfiles.fulfilled,
      (state, { payload: { results = [], count = 0 } }) => {
        state.status = SUCCESS_STATUS
        state.facility_profiles.count = count
        facilityProfileAdapter.setAll(state.facility_profiles, results)
      }
    )
    builder.addCase(searchFacilityProfiles.rejected, state => {
      state.status = FAILURE_STATUS
    })
    builder.addCase(fetchFacilityProfileById.pending, state => {
      state.status = LOADING_STATUS
    })
    builder.addCase(fetchFacilityProfileById.rejected, state => {
      state.status = FAILURE_STATUS
    })
    builder.addCase(fetchFacilityProfileById.fulfilled, (state, { payload }) => {
      const form = payload.form && payload.form.is_active ? payload.form : null
      const forms = payload.form
        ? [payload.form, ...(payload.special_release_forms || [])]
        : [...(payload.special_release_forms || [])]

      facilityProfileAdapter.upsertOne(state.facility_profiles, {
        ...payload,
        form,
        special_release_forms: forms,
      })
      state.status = SUCCESS_STATUS
    })
    builder.addCase(createFacilityProfile.fulfilled, (state, { payload }) => {
      facilityProfileAdapter.upsertOne(state.facility_profiles, payload)
    })
    builder.addCase(updateFacilityProfile.fulfilled, (state, { payload }) => {
      facilityProfileAdapter.updateOne(state.facility_profiles, payload)
    })

    builder.addCase(setFacilityProfileRetriever.fulfilled, (state, { payload }) => {
      facilityProfileAdapter.updateOne(state.facility_profiles, payload)
    })
    builder.addCase(
      setFacilityProfileSpecialReleaseRetriever.fulfilled,
      (state, { payload }) => {
        facilityProfileAdapter.updateOne(state.facility_profiles, payload)
      }
    )
    builder.addCase(deleteAuthForm.fulfilled, (state, { payload }) => {
      const entity = state.facility_profiles.entities[payload.facilityId]
      if (!entity) return

      const changes = {
        ...entity,
        form: entity?.form?.id === payload.formId ? null : entity.form,
        special_release_forms: entity.special_release_forms.filter(
          ({ id }) => id !== payload.formId
        ),
      }

      facilityProfileAdapter.updateOne(state.facility_profiles, {
        id: payload.facilityId,
        changes,
      })
    })
    builder.addCase(fetchFormById.fulfilled, (state, { payload }) => {
      facilityProfileAdapter.updateOne(state.facility_profiles, payload)
    })

    // --- Facility Actions ---
    builder.addCase(fetchFacilityById.pending, state => {
      state.status = LOADING_STATUS
    })
    builder.addCase(fetchFacilityById.fulfilled, (state, { payload }) => {
      facilityAdapter.upsertOne(state, payload)
      state.status = SUCCESS_STATUS
    })
    builder.addCase(fetchFacilityById.rejected, state => {
      state.status = FAILURE_STATUS
    })
    builder.addCase(createFacility.pending, state => {
      state.status = LOADING_STATUS
    })
    builder.addCase(createFacility.fulfilled, facilityAdapter.upsertOne)
    builder.addCase(createFacility.rejected, state => {
      state.status = FAILURE_STATUS
    })
    builder.addCase(updateFacility.fulfilled, facilityAdapter.updateOne)
    builder.addCase(deleteFacility.fulfilled, facilityAdapter.removeOne)
    builder.addCase(deleteSpecialReleaseForm.fulfilled, (state, { payload }) => {
      const entity = state.entities[payload.facilityId]
      if (!entity) return
      const changes = {
        special_release_forms: entity.special_release_forms.filter(
          ({ id }) => id !== payload.formId
        ),
      }
      facilityAdapter.updateOne(state, {
        id: payload.facilityId,
        changes,
      })
    })

    // --- Related Data Actions ---
    builder.addCase(
      getRelatedFacilities.fulfilled,
      (state, { payload: { results = [], count = 0 } }) => {
        state.status = SUCCESS_STATUS
        state.related_facilities.count = count
        facilityAdapter.setAll(state.related_facilities, results)
      }
    )
    builder.addCase(
      getRelatedDoctors.fulfilled,
      (state, { payload: { results = [], count = 0 } }) => {
        state.status = SUCCESS_STATUS
        state.related_doctors.count = count
        facilityAdapter.setAll(state.related_doctors, results)
      }
    )
    builder.addCase(getRelatedFacilityProfileFacilities.pending, state => {
      state.status = LOADING_STATUS
    })
    builder.addCase(
      getRelatedFacilityProfileFacilities.fulfilled,
      (state, { payload: { results = [], count = 0, pageSize, page } }) => {
        state.related_facility_profiles.pageSize = pageSize
        state.related_facility_profiles.page = page
        state.related_facility_profiles.count = count
        facilityAdapter.setAll(state.related_facility_profiles, results)
        state.status = SUCCESS_STATUS
      }
    )
    builder.addCase(getRelatedFacilityProfileFacilities.rejected, state => {
      state.status = FAILURE_STATUS
    })
    builder.addCase(fetchFacilityProfileUserNotesById.pending, state => {
      state.user_notes.status = LOADING_STATUS
      state.user_notes.error = null
    })
    builder.addCase(fetchFacilityProfileUserNotesById.fulfilled, (state, { payload }) => {
      state.user_notes.status = SUCCESS_STATUS
      userNotesAdapter.setAll(state.user_notes, payload.results)
      state.user_notes.count = payload.count
      state.user_notes.currentPage = payload.page
      state.user_notes.currentPageSize = payload.pageSize
    })
    builder.addCase(fetchFacilityProfileUserNotesById.rejected, (state, action) => {
      state.user_notes.status = FAILURE_STATUS
      state.user_notes.error = action.error.message
    })
    builder.addCase(addFacilityProfileUserNote.pending, state => {
      state.user_notes.status = LOADING_STATUS
      state.user_notes.error = null
    })
    builder.addCase(addFacilityProfileUserNote.fulfilled, state => {
      state.user_notes.status = SUCCESS_STATUS
    })
    builder.addCase(addFacilityProfileUserNote.rejected, (state, action) => {
      state.user_notes.status = FAILURE_STATUS
      state.user_notes.error = action.payload || action.error.message
    })
    builder.addCase(approveFacilityProfileUserNote.pending, state => {
      state.user_notes.status = LOADING_STATUS
      state.user_notes.error = null
    })
    builder.addCase(approveFacilityProfileUserNote.fulfilled, state => {
      state.user_notes.status = SUCCESS_STATUS
    })
    builder.addCase(approveFacilityProfileUserNote.rejected, (state, action) => {
      state.user_notes.status = FAILURE_STATUS
      state.user_notes.error = action.payload || action.error.message
    })
    builder.addCase(updateFacilityProfileUserNote.pending, state => {
      state.user_notes.status = LOADING_STATUS
      state.user_notes.error = null
    })
    builder.addCase(updateFacilityProfileUserNote.fulfilled, state => {
      state.user_notes.status = SUCCESS_STATUS
    })
    builder.addCase(updateFacilityProfileUserNote.rejected, (state, action) => {
      state.user_notes.status = FAILURE_STATUS
      state.user_notes.error = action.payload || action.error.message
    })
    builder.addCase(deleteFacilityProfileUserNoteById.pending, state => {
      state.user_notes.status = LOADING_STATUS
      state.user_notes.error = null
    })
    builder.addCase(deleteFacilityProfileUserNoteById.fulfilled, state => {
      state.user_notes.status = SUCCESS_STATUS
    })
    builder.addCase(deleteFacilityProfileUserNoteById.rejected, (state, action) => {
      state.user_notes.status = FAILURE_STATUS
      state.user_notes.error = action.payload || action.error.message
    })
  },
})

const { reducer } = facilitySlice

export default reducer
